import { useRef } from 'preact/hooks'

/**
 * Keeps keyboard focus on an item as it moves through a reorderable list.
 *
 * Reordering re-renders the list and replaces the row nodes, so the grip the user was holding stops
 * existing and focus falls back to the document body. Without this, Arrow Up / Arrow Down moves the
 * item exactly once and then the user has to tab all the way back in to carry on — which makes
 * keyboard reordering technically present but not really usable.
 *
 * Restores on the next frame, once the moved item's grip exists at its new position.
 *
 * Grips opt in with `data-reorder-grip`, and lookup is scoped to `listRef` rather than the document
 * because a single page can show more than one reorderable list — an online event renders the agenda
 * and the speaker list side by side.
 *
 * @example
 * const { listRef, focusAfterMove } = useReorderFocus()
 * <div ref={listRef}>
 *   <button data-reorder-grip onKeyDown={…} />
 * </div>
 */
export function useReorderFocus<T extends HTMLElement = HTMLDivElement>() {
  const listRef = useRef<T | null>(null)

  const focusAfterMove = (toIndex: number) => {
    requestAnimationFrame(() => {
      const grips = listRef.current?.querySelectorAll<HTMLElement>('[data-reorder-grip]')
      grips?.[toIndex]?.focus()
    })
  }

  return { listRef, focusAfterMove }
}
